<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { Item } from "../../../types/models";
import { isEmpty } from "../../../utils/CommonFunctions";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import IconComponent from "../common/IconComponent.vue";
import Buttons from "../form/Buttons.vue";
import ModalFormTemplate from "./ModalFormTemplate.vue";
import DataTable from "../../table/DataTable.vue";

const props = defineProps<{
  items?: any | null;
  warehouses_id?: number | string | null | undefined;
  price_lists_id?: number | string;
  showQuantityInput?: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: Item): void;
  (e: "selectMany", item: Item[]): void;
}>();

const code = ref("");
const itemList = ref<Item[]>([]);

watch(
  () => props.items,
  (newValue) => {
    if (newValue) {
      itemList.value = props.items ?? [];
    }
  },
  { immediate: true }
);
onMounted(() => {
  itemList.value = props.items ?? [];
  itemList.value.forEach((item) => {
    item.quantity = 0;
  });
});

const handleItemCodeEnter = async () => {
  if (isEmpty(code.value)) return;
  const params = {
    search_query: code.value,
    price_lists_id: props.price_lists_id,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    itemList.value = response.data;
  } else {
    Toast.show("No items found", "warning");
  }
};
const handleItemSelect = (item: Item) => {
  console.log(item);
  emit("select", item);
  emit("close");
};

const handleItemSelectAll = () => {
  const items = itemList.value.filter((item) => item.quantity > 0);
  if (items.length === 0) {
    Toast.show("Please select at least one item", "warning");
    return;
  }
  emit('selectMany', items);
};
</script>

<template>
  <ModalFormTemplate
    mode="view"
    width="50rem"
    title="Select Item"
    endPoint=""
    @save="handleItemSelectAll"
    :ignore-permission="true"
    @close="$emit('close')"
  >
    <template #header>
      <div class="relative">
        <input
          id="item-search"
          v-model="code"
          type="text"
          class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
          @keyup.enter="handleItemCodeEnter"
        />
        <button
          @click="handleItemCodeEnter"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Search items"
        >
          <IconComponent name="Search" color="gray" />
        </button>
      </div>
      <Buttons caption="Select All" fa-icon="check-doublt" @click="handleItemSelectAll" />
    </template>
    <template #form>
      <DataTable
        :rows="itemList"
        title="Assigned Variant List"
        :fields="[
          {
            name: 'photo',
            caption: 'Photo',
            sortable: false,
            fieldType: 'image',
          },
          { name: 'code', caption: 'Code', sortable: false, fieldType: 'text' },
          { name: 'name', caption: 'Name', sortable: false, fieldType: 'text' },
          {
            name: 'on_hand',
            caption: 'On Hand',
            sortable: false,
            fieldType: 'number',
          },
          {
            name: 'quantity',
            caption: 'Quantity',
            sortable: false,
            fieldType: props.showQuantityInput ? 'number-input' : 'hidden',
          },
          {
            name: 'variant_name',
            caption: 'Variant Name',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'tracked_by',
            caption: 'Tracked By',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'cost',
            caption: 'Cost',
            sortable: false,
            fieldType: 'number',
          },
          {
            name: 'price',
            caption: 'Price',
            sortable: false,
            fieldType: 'number',
          },
        ]"
        :actions="[
          {
            name: 'select',
            permission: 'view',
            icon: 'MousePointer2',
            variant: 'primary',
            title: 'Select',
            visible: () => true,
            ignorePermission: true,
            hideMobile: true,
          },
        ]"
        @action="
          (action, data) => {
            if (action === 'select') {
              handleItemSelect(data);
            }
          }
        "
        :filterFields="[]"
        :show-header="false"
        :show-title="false"
        :show-quick-tour="false"
        @rowDblClick="handleItemSelect"
      />
    </template>
  </ModalFormTemplate>
</template>
